export interface MatchWeights {
  amount: number;
  date: number;
  narration: number;
}

export interface Transaction {
  id: string;
  date: string;
  amount: number;
  narration: string;
  status: string;
}

export interface Match {
  id: string;
  bankTxn: Transaction;
  tallyEntry: Transaction & { ledger: string };
  score: number;
  reasons: string[];
}

export function calculateLevenshteinDistance(str1: string, str2: string): number {
  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();
  const matrix: number[][] = [];

  for (let i = 0; i <= s2.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= s1.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= s2.length; i++) {
    for (let j = 1; j <= s1.length; j++) {
      if (s2.charAt(i - 1) === s1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[s2.length][s1.length];
}

export function calculateTextSimilarity(str1: string, str2: string): number {
  if (!str1 || !str2) return 0;
  
  const distance = calculateLevenshteinDistance(str1, str2);
  const maxLength = Math.max(str1.length, str2.length);
  
  if (maxLength === 0) return 1;
  return 1 - distance / maxLength;
}

export function findMatches(
  bankTransactions: Transaction[],
  tallyEntries: (Transaction & { ledger: string })[],
  weights: MatchWeights,
  minScore: number = 0.5
): Match[] {
  const matches: Match[] = [];
  const availableBank = bankTransactions.filter(b => b.status === 'unmatched');
  const availableTally = tallyEntries.filter(t => t.status === 'unmatched');

  availableBank.forEach(bank => {
    availableTally.forEach(tally => {
      let score = 0;
      const reasons: string[] = [];

      // Amount matching
      if (bank.amount === tally.amount) {
        score += weights.amount;
        reasons.push('Exact Amount Match');
      } else if (Math.abs(bank.amount - tally.amount) < 10) {
        score += weights.amount * 0.8;
        reasons.push('Close Amount Match');
      }

      // Date matching
      const bankDate = new Date(bank.date);
      const tallyDate = new Date(tally.date);
      const daysDiff = Math.abs((bankDate.getTime() - tallyDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysDiff === 0) {
        score += weights.date;
        reasons.push('Same Date');
      } else if (daysDiff <= 3) {
        score += weights.date * (1 - daysDiff / 3) * 0.7;
        reasons.push(`${Math.round(daysDiff)} Day(s) Apart`);
      }

      // Text similarity matching
      const textSim = calculateTextSimilarity(bank.narration, tally.narration);
      if (textSim > 0.6) {
        score += weights.narration * textSim;
        reasons.push(`${Math.round(textSim * 100)}% Text Match`);
      }

      if (score >= minScore) {
        matches.push({
          id: `${bank.id}-${tally.id}`,
          bankTxn: bank,
          tallyEntry: tally,
          score,
          reasons
        });
      }
    });
  });

  return matches.sort((a, b) => b.score - a.score);
}
